import React, { useState, useRef, useEffect } from "react";
import Styles from "./chat.module.css";
import { IoMdSend } from "react-icons/io";
import { CiCamera } from "react-icons/ci";
import CloseIcon from "../../assets/Xclose.svg";
import { Socket } from "socket.io-client";
import api, { apiBaseUrl } from "../../utils/axios/api";
import defaultAvatar from "../../assets/defaultAvatar.svg";

/** Dados públicos do usuário vindos do histórico / POST /chat (sem senha). */
interface ChatUserPreview {
  id?: string;
  name?: string;
  image?: string | null;
  role?: string;
}

interface Message {
  id: string;
  message: string;
  receiverUserId: string;
  senderUserId: string;
  productId?: string;
  orderId?: string;
  chatType: "PRODUCT" | "ORDER";
  read: boolean;
  createdAt: string;
  fileId?: string;
  file?: string;
  /** Alguns fluxos de socket enviam URL/path pronto */
  senderImage?: string;
  sender?: ChatUserPreview;
  receiver?: ChatUserPreview;
}

function resolveProfileImageUrl(
  raw: string | null | undefined
): string | undefined {
  if (raw == null || String(raw).trim() === "") return undefined;
  const s = String(raw).trim();
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  const path = s.replace(/^\/+/, "");
  const encoded = path
    .split("/")
    .map((seg) => encodeURIComponent(seg))
    .join("/");
  return `${apiBaseUrl}/file/${encoded}`;
}

function avatarForMessage(message: Message): string {
  return (
    resolveProfileImageUrl(message.senderImage ?? message.sender?.image) ??
    defaultAvatar
  );
}

interface ChatProps {
  socket: Socket;
  type: "PRODUCT" | "ORDER";
  id: string | undefined;
  onClose: () => void;
  infos: {
    otherUserId: string | null;
    userName: string | null;
  };
}

const Chat: React.FC<ChatProps> = ({ socket, type, id, onClose, infos }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const userId = localStorage.getItem("userId");
  const otherUserId = infos.otherUserId;
  const userName = infos.userName;

  const handleIncomingMessage = (message: Message) => {
    console.log("Nova mensagem recebida:", message);
    setMessages((prevMessages) => {
      if (prevMessages.some((msg) => msg.id === message.id)) {
        return prevMessages;
      }
      return [...prevMessages, message];
    });
  };

  useEffect(() => {
    const fetchChatHistory = async () => {
      if (userId && otherUserId) {
        try {
          const response = await api.get(
            `/chat/history?userId=${userId}&otherUserId=${otherUserId}&type=${type}&id=${id}`
          );

          console.log("Resposta do backend:", response.data.role);
          setMessages(response.data.chats);
          setUserRole(response.data.role);
          /*  await api.post("/chat/markAsRead", { userId, chatId: id }); */
        } catch (error: any) {
          if (error.response && error.response.status === 404) {
            setMessages([]);
          } else {
            console.error("Erro ao buscar histórico de chats:", error);
            setMessages([]);
          }
        }
      }
    };

    fetchChatHistory();

    const chatType = type;
    socket.emit("join room", { chatType, id });

    socket.on("chat message", handleIncomingMessage);

    socket.on("error", (error: any) => {
      console.error("Erro no Socket.IO:", error);
    });

    return () => {
      socket.off("chat message", handleIncomingMessage);
      socket.off("error");
      socket.emit("leave room", { chatType, id });
    };
  }, [socket, userId, otherUserId, type, id]);

  useEffect(() => {
    // Scroll automático para a última mensagem
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleImageClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Modify the handleSendMessage function to accept an optional file parameter
  const handleSendMessage = async (selectedFile: File | null = null) => {
    if (inputValue.trim() !== "" || selectedFile) {
      try {
        const formData = new FormData();
        formData.append("type", type);
        formData.append("id", id || "");
        formData.append("message", inputValue);
        formData.append("senderUserId", userId || "");
        formData.append("receiverUserId", otherUserId || "");

        if (selectedFile) {
          formData.append("file", selectedFile);
        }

        const response = await api.post("/chat", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (response.data) {
          console.log("Resposta do backend:", response.data);

          socket.emit("chat message", response.data);
        } else {
          console.error(
            "Resposta do backend está no formato incorreto:",
            response.data
          );
        }

        // Limpar o input e o arquivo
        setInputValue("");
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } catch (error: any) {
        console.error("Erro ao enviar mensagem:", error);
      }
    }
  };

  // Update handleFileChange to call handleSendMessage with the selected file
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      handleSendMessage(selectedFile);
    }
  };
  const handleCloseChat = () => {
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSendMessage();
    }
  };

  return (
    <div>
      <div className={Styles.overlay} onClick={handleCloseChat}></div>
      <div className={Styles.chatContainer}>
        <div className={Styles.container}>
          <img
            src={CloseIcon}
            alt="Fechar"
            className={Styles.closeIcon}
            onClick={handleCloseChat}
          />
          <h2 className={Styles.h2custom}>{userName}</h2>
          <div className={Styles.chat_wrapper}>
            <div className={Styles.chat_messages}>
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={Styles.messageContainer}
                  style={{
                    flexDirection:
                      message.senderUserId === userId ? "row-reverse" : "row",
                    justifyContent:
                      message.senderUserId === userId
                        ? "flex-start"
                        : "flex-end",
                  }}
                >
                  {
                    <img
                      src={avatarForMessage(message)}
                      alt="Profile"
                      className={`${Styles.profileImage} ${
                        message.senderUserId === userId
                          ? Styles.userMessageImage
                          : Styles.otherMessageImage
                      }}
                    `}
                    />
                  }
                  <div
                    className={
                      message.senderUserId === userId
                        ? Styles.userMessage
                        : Styles.otherMessage
                    }
                  >
                    <p className={Styles.messages}>{message.message}</p>

                    {message.fileId &&
                      (() => {
                        const fileExtension = message.file
                          ?.split(".")
                          .pop()
                          ?.toLowerCase();
                        const isVideo = ["mp4", "webm", "ogg"].includes(
                          fileExtension || ""
                        );
                        const isImage = [
                          "jpg",
                          "jpeg",
                          "png",
                          "gif",
                          "bmp",
                          "svg",
                        ].includes(fileExtension || "");

                        if (isVideo) {
                          return (
                            <video
                              src={`${apiBaseUrl}/file/${message.file}`}
                              controls
                              className={Styles.messageFile}
                            />
                          );
                        } else if (isImage) {
                          return (
                            <img
                              src={`${apiBaseUrl}/file/${message.file}`}
                              alt="Mensagem com arquivo"
                              className={Styles.messageFile}
                            />
                          );
                        } else {
                          const fileName =
                            message.file?.split("/").pop() || "arquivo";
                          return (
                            <a
                              href={`${apiBaseUrl}/chat/file/${message.file}`}
                              download={fileName}
                            >
                              Baixar arquivo
                            </a>
                          );
                        }
                      })()}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            <div className={Styles.chat_input}>
              <div className={Styles.input_container}>
                <input
                  type="text"
                  placeholder="Digite uma mensagem..."
                  value={inputValue}
                  onChange={handleMessageChange}
                  onKeyDown={handleKeyPress}
                />
                <CiCamera
                  className={Styles.attachIcon}
                  onClick={handleImageClick}
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                <IoMdSend
                  className={Styles.sendIcon}
                  onClick={() => handleSendMessage()}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
